import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  TouchableOpacity,
  Text,
  Modal,
  Image,
  Dimensions,
  ActivityIndicator,
  PanResponder,
} from 'react-native';
import { FontAwesome, MaterialIcons } from '@expo/vector-icons';
import { isWeb } from '../utils/platform';
import { uploadImageToS3 } from '../services/s3Upload';

const screenWidth = Math.round(Dimensions.get('window').width);
const screenHeight = Math.round(Dimensions.get('window').height);

/**
 * Tela de câmera reutilizável para nativo (iOS/Android, via expo-camera) e
 * para navegador (via getUserMedia + <video>/<canvas>).
 *
 * Props:
 *  - navigation: objeto de navegação do react-navigation
 *  - fileName: nome do arquivo a ser salvo no S3 (sem extensão)
 *  - onUploaded(url): chamado com a URL pública após o upload ter sucesso
 *  - onCancel(): chamado ao voltar sem salvar (default: navigation.goBack)
 */
export default function CameraCapture({ navigation, fileName, onUploaded, onCancel }) {
  const [hasPermission, setHasPermission] = useState(isWeb ? true : null);
  const [flashMode, setFlashMode] = useState('off');
  const [capturedPhoto, setCapturedPhoto] = useState(null); // uri (native) ou blob-url (web)
  const [capturedBlob, setCapturedBlob] = useState(null); // Blob (somente web)
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // --- refs nativos ---
  const camRef = useRef(null);
  const [focusDepth, setFocusDepth] = useState(0.5);
  const [zoom, setZoom] = useState(0.0005);

  // --- refs web ---
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  // Módulos nativos (expo-camera etc.) são carregados dinamicamente e apenas
  // fora do web, para não entrarem no bundle do navegador.
  const nativeModules = useRef({});

  useEffect(() => {
    if (isWeb) {
      startWebCamera();
      return () => stopWebCamera();
    }
    (async () => {
      const [{ Camera }, ImageManipulator, ImagePicker] = await Promise.all([
        import('expo-camera/legacy'),
        import('expo-image-manipulator'),
        import('expo-image-picker'),
      ]);
      nativeModules.current = { Camera, ImageManipulator, ImagePicker };
      const { status } = await Camera.requestCameraPermissionsAsync();
      setHasPermission(status === 'granted');
    })();
  }, []);

  async function startWebCamera() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setHasPermission(true);
    } catch (err) {
      console.warn('Não foi possível acessar a câmera do navegador:', err);
      setHasPermission(false);
    }
  }

  function stopWebCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }

  async function takePicture() {
    setLoading(true);
    if (isWeb) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          setCapturedBlob(blob);
          setCapturedPhoto(URL.createObjectURL(blob));
          setLoading(false);
          setOpen(true);
        },
        'image/jpeg',
        0.9
      );
    } else {
      const data = await camRef.current.takePictureAsync({ quality: 0.5 });
      setLoading(false);
      setCapturedPhoto(data.uri);
      setOpen(true);
    }
  }

  function pickImageGalery() {
    if (isWeb) {
      fileInputRef.current && fileInputRef.current.click();
      return;
    }
    (async () => {
      const { ImagePicker } = nativeModules.current;
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.All,
        allowsEditing: true,
        aspect: [4, 6],
        quality: 1,
      });
      if (!result.canceled && result.assets && result.assets[0]) {
        setCapturedPhoto(result.assets[0].uri);
        setOpen(true);
      }
    })();
  }

  function handleWebFileChange(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setCapturedBlob(file);
    setCapturedPhoto(URL.createObjectURL(file));
    setOpen(true);
    e.target.value = '';
  }

  async function uploadPicture() {
    setLoading(true);
    try {
      let url;
      if (isWeb) {
        url = await uploadImageToS3({
          blob: capturedBlob,
          fileName,
          mimeType: 'image/jpeg',
        });
      } else {
        const { ImageManipulator } = nativeModules.current;
        const manipImage = await ImageManipulator.manipulateAsync(capturedPhoto, [
          { resize: { width: 2000 } },
        ]);
        url = await uploadImageToS3({
          uri: manipImage.uri,
          fileName,
          mimeType: 'image/jpeg',
        });
      }
      setLoading(false);
      setOpen(false);
      stopWebCamera();
      onUploaded && onUploaded(url);
    } catch (err) {
      setLoading(false);
      alert('Erro ao enviar imagem: ' + err.message);
    }
  }

  function handleGoBack() {
    stopWebCamera();
    if (onCancel) onCancel();
    else navigation.goBack(null);
  }

  const handleTapToFocus = (e) => {
    const y = e.nativeEvent.locationY / screenHeight;
    setFocusDepth(Math.max(0, Math.min(1, y)));
  };

  if (hasPermission === null) {
    return <View />;
  }
  if (hasPermission === false) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <Text style={{ textAlign: 'center' }}>
          Não foi possível acessar a câmera. Verifique as permissões do navegador/dispositivo.
        </Text>
        <TouchableOpacity onPress={handleGoBack} style={{ marginTop: 20 }}>
          <Text style={{ color: '#003c84' }}>Voltar</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: 'transparent', flexDirection: 'row' }}>
      {isWeb ? (
        <View style={{ flex: 1 }}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', height: '100%', objectFit: 'cover', backgroundColor: '#000' }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleWebFileChange}
          />
          {/* <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleWebFileChange}
          /> */}
        </View>
      ) : nativeModules.current.Camera ? (
        <nativeModules.current.Camera
          style={{ flex: 1 }}
          ref={camRef}
          flashMode={flashMode}
          zoom={zoom}
          focusDepth={focusDepth}
        />
      ) : (
        <View style={{ flex: 1 }} />
      )}

      <View
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        {...(!isWeb
          ? PanResponder.create({
              onStartShouldSetPanResponder: () => true,
              onPanResponderGrant: handleTapToFocus,
            }).panHandlers
          : {})}
      >
        {!isWeb && (
          <TouchableOpacity
            onPress={() => setFlashMode(flashMode === 'off' ? 'on' : 'off')}
            style={{ position: 'absolute', left: '5%', top: '10%', borderRadius: 50, height: 25, width: 25 }}
          >
            {flashMode === 'off' && <MaterialIcons name="flash-off" size={30} color="#FFFFFF" />}
            {flashMode === 'on' && <MaterialIcons name="flash-on" size={30} color="#FFFFFF" />}
          </TouchableOpacity>
        )}

        {!loading && (
          <TouchableOpacity
            style={{ position: 'absolute', bottom: 20, left: screenWidth / 2 - 60 }}
            onPress={takePicture}
          >
            <FontAwesome name="camera" size={30} color="#FFFFFF" />
            <Text style={{ fontSize: 18, marginBottom: 10, color: 'white' }}>Tirar Foto</Text>
          </TouchableOpacity>
        )}
        {loading && (
          <ActivityIndicator
            size="large"
            color="#fff"
            style={{ position: 'absolute', bottom: 35, left: screenWidth / 2 - 20 }}
          />
        )}

        <TouchableOpacity style={{ position: 'absolute', bottom: 20, left: 30 }} onPress={handleGoBack}>
          <FontAwesome name="close" size={30} color="#FFFFFF" />
          <Text style={{ fontSize: 18, marginBottom: 10, color: 'white' }}>Voltar</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={{ position: 'absolute', bottom: 20, right: 30 }}
          onPress={pickImageGalery}
        >
          <FontAwesome name="image" size={30} color="#FFFFFF" />
          <Text style={{ fontSize: 18, marginBottom: 10, color: 'white' }}>Galeria</Text>
        </TouchableOpacity>
      </View>

      {capturedPhoto && (
        <Modal animationType="slide" transparent={false} visible={open}>
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', margin: 20 }}>
            <Image
              style={{ height: screenHeight * 0.6, width: screenWidth * 0.9, borderRadius: 20 }}
              source={{ uri: capturedPhoto }}
            />
            <View style={{ flexDirection: 'row' }}>
              <TouchableOpacity style={{ margin: 10 }} onPress={() => setOpen(false)}>
                <FontAwesome name="window-close" size={50} color="#484848" />
                <Text style={{ fontSize: 18, marginBottom: 10, color: 'black' }}>Alterar</Text>
              </TouchableOpacity>
              {!loading && (
                <TouchableOpacity style={{ margin: 10 }} onPress={uploadPicture}>
                  <FontAwesome name="save" size={50} color="#484848" />
                  <Text style={{ fontSize: 18, marginBottom: 10, color: 'black' }}>Salvar</Text>
                </TouchableOpacity>
              )}
              {loading && <ActivityIndicator size="large" color="#0000ff" />}
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
}
